"use client";
import { ReactNode } from "react";
import { Button, LinkButton } from "@/components/ui/Button";
import Magnetic from "@/components/ui/Magnetic";
import { useTranslations } from "next-intl";
import { useSceneContext } from "@/contexts/SceneContext";
import { openContactModal } from "@/contexts/ContactModalContext";
import { useReducedMotion } from "@/hooks/useFramerMotion";

const GetInTouchButton = ({
  href = "",
  blank = true,
  children,
  className = "",
}: {
  href?: string;
  blank?: boolean;
  children?: ReactNode;
  className?: string;
}) => {
  const { isFooterContactHovered } = useSceneContext();
  const t = useTranslations("common");
  const reduce = useReducedMotion();
  const label = children ? (children as ReactNode) : t("sendMessage");

  // eslint-disable-next-line react-hooks/immutability -- isFooterContactHovered is a SceneContext ref; writing .current in a handler is fine
  const hoverOn = () => (isFooterContactHovered.current = true);
  // eslint-disable-next-line react-hooks/immutability -- ref write in a handler
  const hoverOff = () => (isFooterContactHovered.current = false);

  // On the /contact page the form is rendered inline (#contact-form) — scroll to
  // it instead of opening the modal. Everywhere else, open the modal.
  const handleAction = () => {
    const inlineForm = document.getElementById("contact-form");
    if (inlineForm) {
      inlineForm.scrollIntoView({
        behavior: reduce ? "auto" : "smooth",
        block: "start",
      });
    } else {
      openContactModal();
    }
  };

  // An explicit href still navigates (link behavior preserved).
  if (href) {
    return (
      <div className={`${className} relative`}>
        <Magnetic>
          <LinkButton
            variant="fancy"
            href={href}
            target={blank ? "_blank" : "_self"}
            onMouseEnter={hoverOn}
            onMouseLeave={hoverOff}
            className="relative">
            {label}
          </LinkButton>
        </Magnetic>
      </div>
    );
  }

  // Default "Send Message" → open the space-themed contact modal.
  return (
    <div className={`${className} relative`}>
      <Magnetic>
        <Button
          variant="fancy"
          type="button"
          onClick={handleAction}
          onMouseEnter={hoverOn}
          onMouseLeave={hoverOff}
          className="relative">
          {label}
        </Button>
      </Magnetic>
    </div>
  );
};

export default GetInTouchButton;
